// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import type { ClientPolicyInfo } from '../../types/invitation';
import '../PolicyList.css';

type ClientPolicyTableProps = Readonly<{
  policies: ClientPolicyInfo[];
}>;

const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getOwnershipTypeLabel = (type?: string) => {
  switch (type) {
    case 'SOLO': return 'Solo';
    case 'JOINT': return 'Joint';
    case 'GROUP': return 'Group';
    default: return type || '-';
  }
};

const renderSignatoryStatusTag = (status: string, providerRejected?: boolean) => {
  if (providerRejected) {
    return (
      <span className="policy-status-tag policy-status-tag--error">
        <AppIcon name="rejected" size={16} className="policy-status-tag__icon" />
        Signing rejected
      </span>
    );
  }
  switch (status) {
    case 'PENDING_REVIEW':
    case 'CONFIRMED':
      return (
        <span className="policy-status-tag policy-status-tag--warning">
          <AppIcon name="envelope" size={16} className="policy-status-tag__icon" />
          LoA Invite sent
        </span>
      );
    case 'PENDING_SIGNATURE':
      return (
        <span className="policy-status-tag policy-status-tag--error">
          <AppIcon name="invite not sent" size={16} className="policy-status-tag__icon" />
          Not invited
        </span>
      );
    case 'SIGNED':
      return (
        <span className="policy-status-tag policy-status-tag--success">
          <AppIcon name="checkbox" size={16} className="policy-status-tag__icon" />
          Signed
        </span>
      );
    case 'FLAGGED':
      return (
        <span className="policy-status-tag policy-status-tag--error">
          <AppIcon name="rejected" size={16} className="policy-status-tag__icon" />
          Flagged
        </span>
      );
    default:
      return (
        <span className="policy-status-tag policy-status-tag--error">
          <AppIcon name="invite not sent" size={16} className="policy-status-tag__icon" />
          Invite not sent
        </span>
      );
  }
};

export function ClientPolicyTable({ policies }: ClientPolicyTableProps) {
  if (policies.length === 0) return null;

  return (
    <table className="policy-list-table">
      <thead>
        <tr>
          <th>Provider</th>
          <th>Policy Type</th>
          <th>Policy No.</th>
          <th>Type</th>
          <th className="col-count">Owners</th>
          <th className="col-count">Signatories</th>
          <th>Last updated</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {policies.map((policy) => (
          <tr key={policy.policyId}>
            <td>
              <div className="policy-provider">
                <div className="policy-provider__avatar">
                  {policy.providerLogoUrl ? (
                    <img src={policy.providerLogoUrl} alt={policy.providerName || 'Provider'} />
                  ) : (
                    getInitials(policy.providerName)
                  )}
                </div>
                <span className="policy-provider__name">
                  {policy.providerName || 'Unknown'}
                </span>
              </div>
            </td>
            <td>
              <span className="policy-type">{policy.policyTypeName || '-'}</span>
            </td>
            <td>
              <span className="policy-number">{policy.policyNumber || '-'}</span>
            </td>
            <td>
              <span className="policy-ownership">{getOwnershipTypeLabel(policy.ownershipType)}</span>
            </td>
            <td className="col-count">
              <span className="policy-count-badge policy-count-badge--owners">{policy.ownerCount ?? 0}</span>
            </td>
            <td className="col-count">
              <span className="policy-count-badge policy-count-badge--signatories">{policy.signatoryCount ?? 0}</span>
            </td>
            <td>
              <span className="policy-date">{formatDateTime(policy.updatedAt)}</span>
            </td>
            <td>
              {renderSignatoryStatusTag(policy.signatoryStatus, policy.providerRejected)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
